import emailjs from '@emailjs/browser';
import { GeneratedList } from '../pages/home/page';

// ─────────────────────────────────────────────────────────────────────────────
// CONFIGURACIÓN EmailJS
// Para activar el envío de emails seguí estos pasos:
//   1. Crear cuenta gratuita en https://www.emailjs.com/
//   2. Agregar un "Email Service" (Gmail, Outlook, etc.) → obtener SERVICE_ID
//   3. Crear un "Email Template" con estas variables:
//        {{to_email}}  {{subject}}  {{message}}  {{generated_at}}
//      → obtener TEMPLATE_ID
//   4. En "Account" → "API Keys" → copiar tu Public Key → poner en PUBLIC_KEY
// ─────────────────────────────────────────────────────────────────────────────
const SERVICE_ID = 'service_jkdggk7';
const TEMPLATE_ID = 'template_7y4icg2';
const PUBLIC_KEY = 'k7hUdqNQ8bYgMTk3o';

const DEST_EMAIL = 'clubrayovolantines@gmail.com';

const TYPE_LABELS: Record<string, { label: string; labelPlural: string }> = {
  individual: { label: 'Individual', labelPlural: 'Individuales' },
  dupla: { label: 'Dupla', labelPlural: 'Duplas' },
  trio: { label: 'Trío', labelPlural: 'Tríos' },
};

function formatDateTime(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const d = `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
  const t = `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  return `${d} ${t}`;
}

function buildEmailMessage(lists: GeneratedList[], generatedAt: Date): string {
  const dateStr = formatDateTime(generatedAt);

  const totalGroups = lists.reduce((a, l) => a + l.players.length, 0);

  let msg = '';
  msg += `LISTAS GENERADAS – ${dateStr}\n`;
  msg += '='.repeat(50) + '\n\n';
  msg += `Total de listas: ${lists.length}   |   Total de registros: ${totalGroups}\n\n`;

  // Agrupar por tipo
  const byType: Record<string, GeneratedList[]> = {};
  lists.forEach((l) => {
    if (!byType[l.type]) byType[l.type] = [];
    byType[l.type].push(l);
  });

  (['individual', 'dupla', 'trio'] as const).forEach((type) => {
    const typeLists = byType[type];
    if (!typeLists || typeLists.length === 0) return;
    const meta = TYPE_LABELS[type];

    msg += '─'.repeat(50) + '\n';
    msg += `  ★ ${meta.labelPlural.toUpperCase()} (${typeLists.length} lista${typeLists.length !== 1 ? 's' : ''})\n`;
    msg += '─'.repeat(50) + '\n\n';

    typeLists.forEach((list) => {
      msg += `  Lista N° ${list.listNumber}  (${list.players.length} ${meta.label.toLowerCase()}${list.players.length !== 1 ? 's' : ''})\n`;
      msg += '  ' + '-'.repeat(40) + '\n';

      list.players.forEach((player, idx) => {
        msg += `  ${String(idx + 1).padStart(2, '0')}. ${player.jugador1}`;
        if (type === 'dupla' || type === 'trio') msg += ` / ${player.jugador2}`;
        if (type === 'trio') msg += ` / ${player.jugador3}`;
        msg += `  [${player.club} – ${player.categoria}]\n`;
      });

      msg += '\n';
    });
  });

  msg += '='.repeat(50) + '\n';
  msg += `Generado automáticamente por el Sistema Club Rayo`;
  return msg;
}

export interface EmailResult {
  success: boolean;
  error?: string;
}

export async function sendListsEmail(lists: GeneratedList[]): Promise<EmailResult> {
  if (!PUBLIC_KEY || PUBLIC_KEY === 'TU_PUBLIC_KEY_EMAILJS') {
    return {
      success: false,
      error: 'EMAIL_NOT_CONFIGURED',
    };
  }

  const generatedAt = new Date();
  const dateStr = formatDateTime(generatedAt);
  const message = buildEmailMessage(lists, generatedAt);

  try {
    await emailjs.send(
      SERVICE_ID,
      TEMPLATE_ID,
      {
        to_email: DEST_EMAIL,
        subject: `Club Rayo – Listas Generadas ${dateStr}`,
        message,
        generated_at: dateStr,
        total_listas: String(lists.length),
        total_registros: String(lists.reduce((a, l) => a + l.players.length, 0)),
      },
      PUBLIC_KEY
    );
    return { success: true };
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : String(err);
    return { success: false, error: message };
  }
}
